import React, { useImperativeHandle, useRef, forwardRef, useEffect } from 'react';
import './MiddlePanel.css';
import TradingViewLightChart from './TradingViewLightChart';
import WelcomeCard from './WelcomeCard';
import logger from '../../../utils/logger';
import { analytics } from '../../../firebaseConfig';
import { logEvent } from 'firebase/analytics';
import { isCodexUi } from '../../../utils/codexEnv';


const MiddlePanel = forwardRef(({
  isDarkMode,
  selectedCategory,
  selectedInstrument,
  onManageButtons,
  isChatExpanded,
  isExpandedLeftPanel,
  onStartQuickTour,
  disableQuickStart = false,
  onPriceUpdate
}, ref) => {

  const chartRef = useRef(null);

  const handleChartUpdate = (symbol, timeframe, category) => {
    logger.log('Chart updated:', { symbol, timeframe, category });
    
    // Log chart update
    if (!isCodexUi && analytics) {
      logEvent(analytics, 'chart_updated', {
        symbol,
        timeframe,
        category,
        instrument: selectedInstrument,
        chat_expanded: isChatExpanded
      });
    }
  };

  // Track chart load performance
  useEffect(() => {
    const chartLoadStart = Date.now();
    
    return () => {
      const loadTime = Date.now() - chartLoadStart;
      if (!isCodexUi && analytics) {
        logEvent(analytics, 'chart_load_performance', {
          load_time: loadTime,
          instrument: selectedInstrument,
          category: selectedCategory
        });
      }
    };
  }, [selectedInstrument, selectedCategory]);

  // Example footer buttons - customize as needed
  const footerButtons = [
    { label: 'Buy', action: () => logger.log('Buy clicked') },
    { label: 'Sell', action: () => logger.log('Sell clicked') }
  ];

  const chartLines = [
    // { price: 1.2345, type: 'support' },
    // { price: 1.0, type: 'resistance' }
  ];

    // Add function to be exposed via ref
    useImperativeHandle(ref, () => ({
        addLine: (line) => { 
            if (chartRef.current) {
                chartRef.current.addLine(line);
            }
        }
    }));

  const handleQuickStartClick = () => {
    // Log quick start guide click
    if (!isCodexUi && analytics) {
      logEvent(analytics, 'quick_start_guide_clicked', {
        instrument: selectedInstrument,
        category: selectedCategory
      });
    }
    
    // Start the quick start tour
    if (onStartQuickTour) {
      onStartQuickTour();
    }
  };

  // Show welcome card when no instrument is selected
  const showWelcomeCard = !selectedInstrument;

  return (
    <div className={`middle-panel ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className="graph-container">
        {showWelcomeCard ? (
          <WelcomeCard 
            isDarkMode={isDarkMode}
            onQuickStartClick={handleQuickStartClick}
            disableQuickStart={disableQuickStart}
          />
        ) : (
          <TradingViewLightChart
            darkMode={isDarkMode}
            onChartUpdate={handleChartUpdate}
            onPriceUpdate={onPriceUpdate}
            footerButtons={footerButtons}
            onManageButtons={onManageButtons}
            selectedCategory={selectedCategory}
            selectedInstrument={selectedInstrument}
            lines={chartLines}
            isChatExpanded={isChatExpanded}
            isExpandedLeftPanel={isExpandedLeftPanel}
            ref={chartRef}
          />
        )}
      </div>
    </div>
  );
});

MiddlePanel.displayName = 'MiddlePanel';

export default MiddlePanel; 
